import React, { useEffect, useState } from "react";
import SEO from "../component/SEO";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { CheckCircle, XCircle, Mail, Phone } from "lucide-react";

const Profile = () => {
  const [user, setUser] = useState(null);
  const token = useSelector((state) => state.user.token);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        if (!res.ok) toast.error(data.message || "Failed to fetch profile");
        else setUser(data);
      } catch (err) {
        console.error("❌ Profile fetch error:", err);
        toast.error("Server error while fetching profile");
      }
    };
    fetchProfile();
  }, [token]);

  if (!token)
    return (
      <div className="text-center mt-10 text-lg text-red-500">
        You need to login to see your profile.
      </div>
    );

  if (!user)
    return (
      <div className="text-center mt-10 text-lg">Loading profile...</div>
    );

  return (
    <div className="max-w-md mx-auto mt-10 mb-10 p-6 bg-white rounded-xl shadow-lg border border-gray-200">
      <SEO title="Your Profile - K Bakery" description="Manage your K Bakery profile." />
      <h1 className="text-3xl font-bold text-pink-600 mb-6 text-center">
        Your Profile
      </h1>

      {/* Personal Info */}
      <div className="mb-4">
        <h3 className="text-xl font-semibold text-gray-700 mb-2">Personal Info</h3>
        <div className="grid grid-cols-1 gap-2">
          <div>
            <span className="text-gray-500">First Name:</span>{" "}
            <span className="font-medium text-gray-800">{user.firstName || "-"}</span>
          </div>
          <div>
            <span className="text-gray-500">Last Name:</span>{" "}
            <span className="font-medium text-gray-800">{user.lastName || "-"}</span>
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="mb-4">
        <h3 className="text-xl font-semibold text-gray-700 mb-2">Contact Info</h3>
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-500" />
            <span className="text-gray-800">{user.email || "-"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-500" />
            <span className="text-gray-800">{user.phoneNumber || "-"}</span>
          </div>
        </div>
      </div>

      {/* Verification */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-700 mb-2">Verification</h3>
        <div className="flex items-center gap-2">
          {user.emailVerified ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          <span className={user.emailVerified ? "text-green-600" : "text-red-600"}>
            Email {user.emailVerified ? "Verified" : "Not Verified"}
          </span>
        </div>
      </div>

      {/* Change Password */}
      <div className="border-t pt-4">
        <h3 className="text-xl font-semibold text-gray-700 mb-4">Change Password</h3>
        <form onSubmit={async (e) => {
          e.preventDefault();
          const currentPassword = e.target.currentPassword.value;
          const newPassword = e.target.newPassword.value;
          
          if (!currentPassword || !newPassword) return toast.error("Please fill all fields");
          
          try {
            const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/change-password`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({ currentPassword, newPassword })
            });
            const data = await res.json();
            if (res.ok) {
              toast.success(data.message);
              e.target.reset();
            } else {
              toast.error(data.message);
            }
          } catch (err) {
            toast.error("Failed to change password");
          }
        }} className="flex flex-col gap-3">
          <input type="password" name="currentPassword" placeholder="Current Password" required className="p-2 border rounded" />
          <input type="password" name="newPassword" placeholder="New Password" required minLength={6} className="p-2 border rounded" />
          <button type="submit" className="bg-purple-500 hover:bg-purple-600 text-white p-2 rounded w-full sm:w-auto self-start">Update Password</button>
        </form>
      </div>

    </div>
  );
};

export default Profile;
